import React from 'react';

import Text from 'common/components/Text/Text';
import styles from './Shortlist.module.scss';

import {
  useAddPostulationMutation,
  useGetFavoritePostulationQuery,
  useGetBackofficeFavoritePostulationQuery,
  useAddBackofficePostulationMutation,
} from 'services/exploreApi';
import { shortlistLabelCard } from 'common/utils';

import { useMediaQuery } from 'react-responsive';
import { scheduleConversion, formatNumber, newDetailUrl } from 'common/utils/conversions';
import { Program, ApiObject, Postulation, School } from 'app/types';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { setApplicationStarted, setLastStepDone } from 'features/user/userSlice';
import { useAppDispatch } from 'app/hooks';

interface CardProps {
  program: Program;
}

const FavoriteCard = ({ program }: CardProps): React.ReactElement => {
  const [submitPostulation] = useAddPostulationMutation();
  const [submitBackofficePostulation] = useAddBackofficePostulationMutation();
  const location = useLocation();
  let id: string | undefined;
  let postulation: ApiObject<Postulation> | undefined;
  const isBackoffice = location.pathname.includes('backoffice');
  if (isBackoffice) {
    ({ id } = useParams<{ id: string }>());
    ({ data: postulation } = useGetBackofficeFavoritePostulationQuery({
      program_id: program.id || 1,
      id: parseInt(id || '1'),
    }));
  } else {
    ({ data: postulation } = useGetFavoritePostulationQuery(program.id));
  }
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const handleSubmit = () => {
    if (postulation) {
      navigate(`applications/${postulation.data.id}`);
    } else {
      if (isBackoffice) {
        const values = { program_id: program.id, user_id: parseInt(id || '1') };
        submitBackofficePostulation(values).then((resp: any) => {
          navigate(`applications/${resp.data.data.id}`);
        });
      } else {
        const values = { program_id: program.id };
        submitPostulation(values).then((resp: any) => {
          dispatch(setApplicationStarted(true));
          dispatch(setLastStepDone(6));
          navigate(`applications/${resp.data.data.id}`);
        });
      }
    }
  };

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const label = shortlistLabelCard(program.preferences_score);

  return (
    <div className={styles.shortlistCard}>
      <div>
        <div>
          <Text
            size={isMobile ? 14 : 16}
            weight={600}
            inlineStyle={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {program.name}
          </Text>
        </div>
        <Text
          size={isMobile ? 14 : 16}
          weight={400}
          inlineStyle={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {program.school_name}
        </Text>
      </div>
      {label && (
        <div className={`${styles.labelContainer}`}>
          <div
            className={`${styles.labelStatus}`}
            style={{ backgroundColor: label.background, width: label.width }}
          >
            <div className={`${styles.statusText}`} style={{ color: label.text }}>
              {label.title}
            </div>
          </div>
        </div>
      )}
      <div>
        {program.study_options_first?.tuition_fee &&
          program.study_options_first.tuition_fee.amount_international !== null &&
          (program.study_options_first.tuition_fee.amount_international > 0 ? (
            <>
              {program.many_study_options ? (
                <>
                  <Text size={isMobile ? 12 : 14} inline>
                    From{' '}
                    <Text size={isMobile ? 12 : 14} weight={500} inline>
                      {formatNumber(program.tuition_min)}
                    </Text>
                  </Text>
                  <Text
                    size={isMobile ? 12 : 14}
                    weight={500}
                    inline
                    inlineStyle={{ color: '#727D9B' }}
                  >
                    {` ${program.tuition_min_currency}`}
                  </Text>
                </>
              ) : (
                <>
                  <Text size={isMobile ? 12 : 14} weight={500} inline>
                    {formatNumber(program.study_options_first.tuition_fee?.amount_international)}
                  </Text>
                  <Text
                    size={isMobile ? 12 : 14}
                    weight={500}
                    inline
                    inlineStyle={{ color: '#727D9B' }}
                  >
                    {` ${program.study_options_first.tuition_fee.currency}`}
                  </Text>
                </>
              )}
            </>
          ) : (
            <Text size={isMobile ? 12 : 14} weight={400} inline>
              0 tuition fee
            </Text>
          ))}
        <Text
          size={isMobile ? 12 : 14}
          weight={400}
          inlineStyle={{ lineHeight: '24px', marginLeft: '5px', color: '#727D9B' }}
          inline
        >
          {`${
            program.multiple_schedules
              ? 'full-time/part-time'
              : scheduleConversion(program.study_options_first.schedule, true)
          }, ${program.multiple_durations ? 'from' : ''} ${
            program.study_options_first.duration_in_months
          } months`}
        </Text>
        <div className={styles.locationDesktop}>
          <i className="bi bi-geo-alt"></i>
          <Text size={isMobile ? 12 : 14} inline>
            {program.multiple_locations
              ? program.online_delivery
                ? 'Multiple and Online'
                : 'Multiple'
              : `${program.study_options_first.city.name}, ${program.study_options_first.city.country.name}`}
          </Text>
        </div>
      </div>
      {!isMobile && <hr className={`${styles.horizontal}`} />}
      {!program.postulated ? (
        <div className={`${styles.button}`} onClick={handleSubmit}>
          <Text size={isMobile ? 14 : 16} weight={700} color={'#2071F1'}>
            Start application
          </Text>
        </div>
      ) : (
        <div className={`${styles.button}`} onClick={handleSubmit}>
          <Text size={isMobile ? 14 : 16} weight={700} color={'#2071F1'}>
            Enter application
          </Text>
        </div>
      )}
    </div>
  );
};

export const SchoolFavoriteCard = ({ school }: { school: School }): React.ReactElement => {
  return (
    <a
      className={`card ${styles.contentGridCard}`}
      style={{
        backgroundImage: `linear-gradient(rgba(25, 54, 118, 0), rgba(0, 0, 0, 0.6)), url(${school.photos?.[0]})`,
      }}
      href={newDetailUrl('schools', school.id, school.name)}
      target="_blank"
      rel="noreferrer"
    >
      <div className={styles.schoolName}>{school.name}</div>
      <div className={styles.imageContainer}>
        <img src={school.logo} className={styles.img} alt={`${school.name} Logo`} />
      </div>
    </a>
  );
};
export default FavoriteCard;
